import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { cn } from "../lib/utils";
import { differenceInDays, format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Plus,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Check,
  X,
  MessageCircle,
  Pencil,
  Trash2,
  PartyPopper,
  Gift,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  getLeaves,
  applyLeave,
  applyCompensatoryEarn,
  applyMaternityEarn,
  applyPaternityEarn,
  cancelLeave,
  getLeaveBalance,
  updateLeaveStatus,
  getMyLeaves
} from "../api/leaves";
import {
  getLeaveSettings,
  getLeaveBalance as getStructuredLeaveBalance,
  type LeaveSettings as LeaveSettingsData,
  type LeaveBalance as StructuredBalance,
  type LeaveType,
} from "../api/leaveSettings";
import api from "../api/api";
import { useAuth } from "../contexts/AuthContext";
import { useAccess } from "../contexts/AccessContext";
import { useToast } from "../hooks/useToast";
import { Leave } from "../types";

type Holiday = { date: string; name: string };

type ApplyErrorState = {
  open: boolean;
  message: string;
  balance_remaining?: number;
  leave_type?: string;
};

export function Leaves() {
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [allLeaves, setAllLeaves] = useState<Leave[]>([]);
  const [leaveBalance, setLeaveBalance] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [approvalAction, setApprovalAction] = useState<"approved" | "rejected">(
    "approved"
  );
  const [approvalComments, setApprovalComments] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  // Per-tab pagination (1-based). My Leaves, Pending Approvals, All Team.
  const [myLeavesPage, setMyLeavesPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [allTeamPage, setAllTeamPage] = useState(1);
  const [newLeave, setNewLeave] = useState({
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",


  });
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const { user } = useAuth();
  const { toast } = useToast();
  const [managerID, setManagerID] = useState<string | null>(null);
  const [employeeID, setEmployeeID] = useState<string | null>(null);
  const [epmtID, setEpmtID] = useState<string | null>(null);
  // Used to gate the maternity / paternity earn buttons. "female" → only
  // maternity is visible; "male" → only paternity. Falls back to showing
  // neither when the gender is unknown / unset.
  const [userGender, setUserGender] = useState<string | null>(null);
  // Supervisor cannot approve, so the "Pending Approvals" tab is hidden for
  // them — default their view to the all-team-requests tab instead.
  // Employees only have My Leaves / Holidays — land them on My Leaves.
  const [activeTab, setActiveTab] = useState(
    user?.role === "supervisor"
      ? "all"
      : (user?.role === "employee" || user?.role === "intern")
        ? "my"
        : "pending",
  );
  const [tabLoading, setTabLoading] = useState(false);
  const [leaveSettings, setLeaveSettings] = useState<LeaveSettingsData | null>(null);
  const [structuredBalance, setStructuredBalance] = useState<StructuredBalance | null>(null);
  const [applyError, setApplyError] = useState<ApplyErrorState>({ open: false, message: "" });

  // Compensatory earn-request dialog state — separate from the regular
  // Apply-Leave dialog because it doesn't draw down a balance. Same
  // shape as maternity/paternity: employee picks from/to dates + days.
  const [isCompApplyOpen, setIsCompApplyOpen] = useState(false);
  const [compFromDate, setCompFromDate] = useState("");
  const [compToDate, setCompToDate] = useState("");
  const [compDays, setCompDays] = useState<string>("");
  const [compReason, setCompReason] = useState("");
  const [compSubmitting, setCompSubmitting] = useState(false);

  // Maternity / paternity earn-request dialog state — same shape: ask
  // a manager to grant N days of entitlement that lands in the balance
  // on approval, then the employee files normal maternity/paternity
  // leave against that balance.
  const [isMatApplyOpen, setIsMatApplyOpen] = useState(false);
  const [matFromDate, setMatFromDate] = useState("");
  const [matToDate, setMatToDate] = useState("");
  const [matDays, setMatDays] = useState<string>("");
  const [matReason, setMatReason] = useState("");
  const [matSubmitting, setMatSubmitting] = useState(false);

  const [isPatApplyOpen, setIsPatApplyOpen] = useState(false);
  const [patFromDate, setPatFromDate] = useState("");
  const [patToDate, setPatToDate] = useState("");
  const [patDays, setPatDays] = useState<string>("");
  const [patReason, setPatReason] = useState("");
  const [patSubmitting, setPatSubmitting] = useState(false);


  // ── Role-based fallbacks ──────────────────────────────────────────────
  // These describe the *legacy* behavior and are used verbatim whenever a user
  // has no Access profile (hasAccess === false), so nobody's experience changes
  // until an admin assigns/edits a profile.
  const roleIsManager =
    user?.role === "manager" || user?.role === "hr head" || user?.role === "hr admin" || user?.role === "admin" || user?.role === "director" || user?.role === "founder";
  const isSupervisor = user?.role === "supervisor";
  const roleIsTeamViewer = roleIsManager || isSupervisor;
  // Managers/supervisors/employees saw holidays read-only; only the elevated
  // tier could add/edit/remove (manager intentionally excluded).
  const roleCanEditHolidays =
    user?.role === "admin" ||
    user?.role === "hr head" ||
    user?.role === "hr admin" ||
    user?.role === "founder" ||
    user?.role === "director";
  // Everyone except admin could apply for their own leave.
  const roleCanApply = user?.role !== "admin";

  // ── Access-driven gating for the Leaves sub-tabs ──────────────────────
  // Each tab is a leveled feature under the "leaves" panel (see panelCatalog).
  // When the user's profile defines a tab's key we honor it; when it's absent
  // (older profiles, or no profile at all) we fall back to the role logic above
  // — so behavior is identical to before until a profile opts in.
  const { hasAccess, permissions } = useAccess();
  const featVisible = (key: string, fallback: boolean) => {
    const p = hasAccess ? (permissions as Record<string, { visible: boolean; level: string }>)[key] : undefined;
    return p === undefined ? fallback : p.visible === true;
  };
  const featManage = (key: string, fallback: boolean) => {
    const p = hasAccess ? (permissions as Record<string, { visible: boolean; level: string }>)[key] : undefined;
    return p === undefined ? fallback : p.visible === true && p.level === "manage";
  };

  // Tab visibility.
  const showMyTab = featVisible("leaves-my", roleCanApply);
  const showApprovalsTab = featVisible("leaves-approvals", roleIsManager);
  const showTeamTab = featVisible("leaves-team", roleIsTeamViewer);
  const showHolidaysTab = featVisible("leaves-holidays", true);
  // Whether to render the team-oriented multi-tab layout vs the employee layout.
  const showTeamLayout = showApprovalsTab || showTeamTab;

  // Manage capabilities within each tab.
  const canApplyOwn = featManage("leaves-my", roleCanApply);
  const canApprove = featManage("leaves-approvals", roleIsManager);
  // Approve directly from the All-Team list (supervisors stay view-only).
  const canApproveFromTeam = featManage("leaves-team", roleIsManager);
  const canManageHolidays = featManage("leaves-holidays", roleCanEditHolidays);

  // Back-compat aliases used throughout the JSX / data-fetch below.
  const isManager = canApprove;          // "acts on others' leaves"
  const canEditHolidays = canManageHolidays;
  const isTeamViewer = showTeamLayout;   // "sees the team-oriented layout"

  // Keep the selected tab pointing at something visible. The role-based default
  // (set in useState below) may be hidden by an Access profile, and permissions
  // hydrate asynchronously — so re-clamp whenever visibility changes.
  useEffect(() => {
    if (!showTeamLayout) return; // employee layout clamps its own value inline
    const visible = [
      [showApprovalsTab, "pending"],
      [showTeamTab, "all"],
      [showMyTab, "my"],
      [showHolidaysTab, "holidays"],
    ]
      .filter(([v]) => v)
      .map(([, k]) => k as string);
    if (visible.length && !visible.includes(activeTab)) setActiveTab(visible[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTeamLayout, showApprovalsTab, showTeamTab, showMyTab, showHolidaysTab, activeTab]);

  // ── Holidays tab state ────────────────────────────────────────────────
  // Pulled from the same salary-settings endpoint that used to live on the
  // Payroll Settings page. The full settings blob is preserved so we can
  // round-trip it on save without dropping the leave-policy fields that now
  // live in Company Settings.
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayName, setHolidayName] = useState("");
  const [editingHolidayKey, setEditingHolidayKey] = useState<string | null>(null);
  const [holidayError, setHolidayError] = useState("");
  const [holidaySaving, setHolidaySaving] = useState(false);
  const salarySettingsRef = useRef<Record<string, any> | null>(null);

  const normalizeHolidayDates = (list: any[] = []): Holiday[] =>
    list.map((h) =>
      typeof h === "string" ? { date: h, name: "Holiday" } : { date: h.date, name: h.name || "Holiday" },
    );

  const fetchHolidays = async () => {
    try {
      setHolidaysLoading(true);
      const { data } = await api.get("/salary/get_salary_settings");
      if (data?.success) {
        salarySettingsRef.current = data.data || {};
        setHolidays(normalizeHolidayDates(data.data?.holiday_dates));
      }
    } catch (err) {
      console.error("Failed to load holidays", err);
    } finally {
      setHolidaysLoading(false);
    }
  };

  const persistHolidays = async (next: Holiday[]) => {
    setHolidaySaving(true);
    try {
      // Round-trip the rest of the salary settings so we don't blow away the
      // leave-policy fields that now live on the Company Settings page.
      const base = salarySettingsRef.current || {};
      const payload = {
        deduction: base.deduction ?? false,
        carryForward: base.carryForward ?? false,
        daysAllowable: base.daysAllowable ?? 0,
        hourly: base.hourly ?? false,
        monthly: base.monthly ?? true,
        sunday: base.sunday ?? false,
        saturday: base.saturday ?? false,
        lateDeduction: base.lateDeduction ?? false,
        overtimeDeduction: base.overtimeDeduction ?? false,
        rateofOvertime: base.rateofOvertime ?? 1.0,
        allowance: base.allowance ?? false,
        holiday_dates: next,
      };
      const { data } = await api.post("/salary/create_or_update_salary_settings", payload);
      if (data?.success) {
        salarySettingsRef.current = data.data || salarySettingsRef.current;
        setHolidays(normalizeHolidayDates(data.data?.holiday_dates ?? next));
      }
      return true;
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast({
        title: "Couldn't save holiday",
        description: typeof detail === "string" ? detail : err?.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setHolidaySaving(false);
    }
  };

  const resetHolidayForm = () => {
    setHolidayDate("");
    setHolidayName("");
    setEditingHolidayKey(null);
    setHolidayError("");
  };

  const handleSaveHoliday = async () => {
    if (!holidayDate || !holidayName.trim()) {
      setHolidayError("Pick a date and enter a holiday name.");
      return;
    }
    const trimmed = holidayName.trim();
    let next: Holiday[];

    if (editingHolidayKey) {
      const clash = holidays.some(
        (h) => h.date === holidayDate && h.date !== editingHolidayKey,
      );
      if (clash) {
        setHolidayError(`A holiday on ${holidayDate} already exists.`);
        return;
      }
      next = holidays.map((h) =>
        h.date === editingHolidayKey ? { date: holidayDate, name: trimmed } : h,
      );
    } else {
      if (holidays.some((h) => h.date === holidayDate)) {
        setHolidayError(`A holiday on ${holidayDate} already exists.`);
        return;
      }
      next = [...holidays, { date: holidayDate, name: trimmed }];
    }

    const ok = await persistHolidays(next);
    if (ok) {
      resetHolidayForm();
      toast({
        title: editingHolidayKey ? "Holiday updated" : "Holiday added",
        description: `${trimmed} — ${holidayDate}`,
      });
    }
  };

  const handleEditHoliday = (h: Holiday) => {
    setEditingHolidayKey(h.date);
    setHolidayDate(h.date);
    setHolidayName(h.name);
    setHolidayError("");
  };

  const handleRemoveHoliday = async (date: string) => {
    if (editingHolidayKey === date) resetHolidayForm();
    const next = holidays.filter((h) => h.date !== date);
    const ok = await persistHolidays(next);
    if (ok) {
      toast({ title: "Holiday removed", description: date });
    }
  };

  useEffect(() => {
    if (activeTab === "holidays") {
      fetchHolidays();
    }
  }, [activeTab]);



  const initLeavesPage = async () => {
    try {
      setLoading(true);

      let context: any = {};

      if (
        user.role === "employee" ||
        user.role === "intern" ||
        user.role === "supervisor" ||
        user.role === "manager" ||
        user.role === "hr head" ||
        user.role === "hr admin" ||
        user.role === "director" ||
        user.role === "founder"
      ) {
        context = await getLeaveBalance();

        setLeaveBalance(context.balance || {});
        setManagerID(context.managerID);
        setEmployeeID(context.employeeID);
        setEpmtID(context.epmtID);
        setUserGender(context.gender || null);
      }

      try {
        const role = user?.role;
        const [settings, balance] = await Promise.all([
          getLeaveSettings(),
          role === "admin" ? Promise.resolve(null) : getStructuredLeaveBalance(),
        ]);
        setLeaveSettings(settings);
        if (balance) setStructuredBalance(balance);
      } catch (e) {
        console.warn("leave settings/balance fetch failed", e);
      }

      let query = "";

      if (user.role === "admin") {
        query = "";
      } else {
        query = context.managerID;
      }

      console.log("ROLE:", user.role);
      console.log("QUERY SENT TO getLeaves:", query);

      const response = await getLeaves(query);

      if (isTeamViewer) {
        setAllLeaves(response.leaves);
        setLeaves(
          response.leaves.filter(
            (leave) => leave.employeeId === user?._id
          )
        );
      } else {
        setLeaves(response.leaves);
      }

    } catch (err) {
      console.error("initLeavesPage error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initLeavesPage();
  }, []);


  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await getLeaves("");


      if (isTeamViewer) {
        // Managers / supervisors see team leaves
        setAllLeaves(response.leaves);
        // Also set user's own leaves for the "My Leaves" tab
        setLeaves(
          response.leaves.filter((leave) => leave.employeeId === user?._id)
        );
      } else {
        // Employees see only their own leaves
        setLeaves(response.leaves);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMyLeaves = async () => {
    try {
      setTabLoading(true);
      const res = await getMyLeaves();
      setLeaves(res.Leaves);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch my leaves",
        variant: "destructive",
      });
    } finally {
      setTabLoading(false);
    }
  };

  // useEffect(() => {
  //     fetchMyLeaves();
  //   }, []);
  useEffect(() => {
    if (activeTab === "my") {
      fetchMyLeaves();
    }
  }, [activeTab]);



  const fetchLeaveBalance = async () => {
    try {
      if (user?._id) {
        const response = await getLeaveBalance(user._id);
        setLeaveBalance(response.balance);
      }
    } catch (error) {
      console.error("Error fetching leave balance:", error);
    }
  };

  // Wipe every field of the Apply Leave form. Called from every close path
  // (Cancel button, X / Esc / backdrop via onOpenChange, successful submit)
  // so the next time the user opens "Apply for Leave" they start fresh.
  const resetApplyForm = () => {
    setNewLeave({ leaveType: "", startDate: "", endDate: "", reason: "" });
    setStartDate(undefined);
    setEndDate(undefined);
    setStartOpen(false);
    setEndOpen(false);
    setApplyError({ open: false, message: "" });
  };

  const handleApplyLeave = async () => {
    try {
      if (!startDate) {
        toast({
          title: "Error",
          description: "Please select a start date first",
          variant: "destructive",
        });
        return;
      }
      if (!endDate) {
        toast({
          title: "Error",
          description: "Please select an end date",
          variant: "destructive",
        });
        return;
      }
      if (endDate < startDate) {
        toast({
          title: "Error",
          description: "End date must be on or after the start date",
          variant: "destructive",
        });
        return;
      }

      const leaveData = {
        employeeID: employeeID,
        managerID: managerID || "",
        epmtID: epmtID,
        name: user.name,
        fromDate: format(startDate, "yyyy-MM-dd"),
        toDate: format(endDate, "yyyy-MM-dd"),
        totalDays: differenceInDays(endDate, startDate) + 1,
        leaveType: newLeave.leaveType,
        reason: newLeave.reason,
        status: "PENDING",
        appliedOn: new Date().toISOString(),
      };

      await applyLeave(leaveData);

      toast({
        title: "Success",
        description: "Leave request submitted successfully",
      });

      setIsApplyDialogOpen(false);
      resetApplyForm();

      initLeavesPage();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setApplyError({
          open: true,
          message: detail.detail || detail.message || "Leave request blocked",
          balance_remaining: detail.balance_remaining,
          leave_type: detail.leave_type,
        });
        return;
      }
      const message =
        typeof detail === "string" ? detail : error?.message || "Failed to submit leave";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const resetCompForm = () => {
    setCompFromDate("");
    setCompToDate("");
    setCompDays("");
    setCompReason("");
  };

  const resetMatForm = () => {
    setMatFromDate("");
    setMatToDate("");
    setMatDays("");
    setMatReason("");
  };

  const resetPatForm = () => {
    setPatFromDate("");
    setPatToDate("");
    setPatDays("");
    setPatReason("");
  };

  // Compute totalDays from from/to dates whenever they change so the
  // helper text + final submission stay in sync.
  const daysBetween = (from: string, to: string): number => {
    if (!from || !to) return 0;
    const f = new Date(from);
    const t = new Date(to);
    if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return 0;
    const diff = differenceInDays(t, f);
    return diff >= 0 ? diff + 1 : 0;
  };

  const handleApplyParentalEarn = async (
    kind: "maternity" | "paternity",
  ) => {
    const isMat = kind === "maternity";
    const fromDate = isMat ? matFromDate : patFromDate;
    const toDate = isMat ? matToDate : patToDate;
    const daysStr = isMat ? matDays : patDays;
    const reason = isMat ? matReason : patReason;
    const setSubmitting = isMat ? setMatSubmitting : setPatSubmitting;
    const setOpen = isMat ? setIsMatApplyOpen : setIsPatApplyOpen;
    const resetForm = isMat ? resetMatForm : resetPatForm;
    const apiFn = isMat ? applyMaternityEarn : applyPaternityEarn;
    const label = isMat ? "Maternity" : "Paternity";

    if (!fromDate || !toDate) {
      toast({
        title: "Dates required",
        description: "Please select both a from and to date.",
        variant: "destructive",
      });
      return;
    }

    const computedDays = daysBetween(fromDate, toDate);
    const requestedDays = Number(daysStr) > 0 ? Number(daysStr) : computedDays;
    if (requestedDays <= 0) {
      toast({
        title: "Days required",
        description: "Days must be a positive number.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);
      await apiFn({
        employeeID,
        managerID,
        epmtID,
        name: user.name,
        fromDate,
        toDate,
        days: requestedDays,
        reason: reason || undefined,
      });
      toast({
        title: `${label} leave request submitted`,
        description:
          "Your reporting person will review and approve. Once approved, the days will appear in your balance.",
      });
      setOpen(false);
      resetForm();
      initLeavesPage();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message =
        typeof detail === "object" && detail?.detail
          ? detail.detail
          : typeof detail === "string"
            ? detail
            : error?.message || `Failed to submit ${label.toLowerCase()} request`;
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyCompensatory = async () => {
    if (!compFromDate || !compToDate) {
      toast({
        title: "Dates required",
        description: "Please select both a from and to date.",
        variant: "destructive",
      });
      return;
    }

    const computedDays = daysBetween(compFromDate, compToDate);
    const requestedDays = Number(compDays) > 0 ? Number(compDays) : computedDays;
    if (requestedDays <= 0) {
      toast({
        title: "Days required",
        description: "Days must be a positive number.",
        variant: "destructive",
      });
      return;
    }

    try {
      setCompSubmitting(true);
      await applyCompensatoryEarn({
        employeeID,
        managerID,
        epmtID,
        name: user.name,
        fromDate: compFromDate,
        toDate: compToDate,
        days: requestedDays,
        reason: compReason || undefined,
      });
      toast({
        title: "Compensatory leave request submitted",
        description:
          "Your reporting person will review and approve. Once approved, the days will appear in your Compensatory balance.",
      });
      setIsCompApplyOpen(false);
      resetCompForm();
      initLeavesPage();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message =
        typeof detail === "object" && detail?.detail
          ? detail.detail
          : typeof detail === "string"
            ? detail
            : error?.message || "Failed to submit compensatory request";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setCompSubmitting(false);
    }
  };

  const handleCancelLeave = async (leaveId: string) => {
    try {
      await cancelLeave(leaveId);
      toast({
        title: "Success",
        description: "Leave request withdrawn successfully",
      });
      initLeavesPage();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message =
        typeof detail === "string" ? detail : error?.message || "Failed to withdraw leave";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleApproveReject = (
    leave: Leave,
    action: "approved" | "rejected"
  ) => {
    setSelectedLeave(leave);
    setApprovalAction(action);
    setApprovalComments("");
    setIsApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    try {
      if (!selectedLeave) return;

      await updateLeaveStatus(
        selectedLeave._id,
        approvalAction,
        approvalComments
      );

      toast({
        title: "Success",
        description: `Leave request ${approvalAction} successfully`,
      });

      setIsApprovalDialogOpen(false);
      setSelectedLeave(null);
      setApprovalComments("");
      fetchLeaves();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
    }
  };

  const filteredLeaves = leaves.filter((leave) => {
    const statusMatch = filterStatus === "all" || leave.status === filterStatus;
    const typeMatch = filterType === "all" || leave.leaveType === filterType;
    return statusMatch && typeMatch;
  });

  // Reset paginated lists to page 1 whenever the (shared) filters change.
  // Pending Approvals isn't filtered, so its page is just clamped on render.
  useEffect(() => {
    setMyLeavesPage(1);
    setAllTeamPage(1);
  }, [filterStatus, filterType]);

  const filteredAllLeaves = allLeaves.filter((leave) => {
    const statusMatch = filterStatus === "all" || leave.status === filterStatus;
    const typeMatch = filterType === "all" || leave.leaveType === filterType;
    return statusMatch && typeMatch;
  });

  // Pending Approvals lists leaves awaiting THIS user's decision. A manager's
  // own leave routes up to their manager / admin, so it must never appear here
  // as actionable — you cannot approve your own request. (employeeID is the
  // logged-in user's own employee code; null for admins, who see everything.)
  const pendingLeaves = allLeaves.filter(
    (leave) => leave.status === "pending" && leave.employeeId !== employeeID
  );

  const sortedHolidays = [...holidays].sort((a, b) => a.date.localeCompare(b.date));

  const renderHolidaysTab = () => (
    <div className="space-y-4">
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PartyPopper className="h-5 w-5 text-blue-600" />
            Company Holidays
          </CardTitle>
          <CardDescription>
            {canEditHolidays
              ? "Add, edit, or remove the holidays observed by your company."
              : "Holidays observed by your company this year."}
          </CardDescription>
        </CardHeader>

        {canEditHolidays && (
          <CardContent className="space-y-3 pt-0">
            {editingHolidayKey && (
              <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 inline-block">
                Editing — update the date / name and click <em>Update Holiday</em>.
              </p>
            )}
            <div className="flex flex-col md:flex-row gap-2">
              <Input
                type="date"
                value={holidayDate}
                onChange={(e) => {
                  setHolidayDate(e.target.value);
                  setHolidayError("");
                }}
                className="md:flex-1"
              />
              <Input
                type="text"
                placeholder="Holiday name (e.g. Diwali)"
                value={holidayName}
                onChange={(e) => {
                  setHolidayName(e.target.value);
                  setHolidayError("");
                }}
                className="md:flex-1"
              />
              <Button
                onClick={handleSaveHoliday}
                disabled={holidaySaving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {holidaySaving
                  ? "Saving..."
                  : editingHolidayKey
                    ? "Update Holiday"
                    : (<><Plus className="h-4 w-4 mr-1" /> Add Holiday</>)}
              </Button>
              {editingHolidayKey && (
                <Button variant="outline" onClick={resetHolidayForm} disabled={holidaySaving}>
                  Cancel
                </Button>
              )}
            </div>
            {holidayError && (
              <p className="text-xs text-red-600">{holidayError}</p>
            )}
          </CardContent>
        )}
      </Card>

      {holidaysLoading ? (
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </CardContent>
        </Card>
      ) : sortedHolidays.length === 0 ? (
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CalendarIcon className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No holidays added yet
            </h3>
            <p className="text-gray-600 text-center">
              {canEditHolidays
                ? "Use the form above to add your company's first holiday."
                : "Your company hasn't published any holidays yet."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {sortedHolidays.map((h) => {
            const isEditing = editingHolidayKey === h.date;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const holidayDateObj = new Date(h.date);
            holidayDateObj.setHours(0, 0, 0, 0);
            const dayDiff = Math.round(
              (holidayDateObj.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
            );
            const status: "today" | "upcoming" | "passed" =
              dayDiff === 0 ? "today" : dayDiff > 0 ? "upcoming" : "passed";
            const statusLabel =
              status === "today"
                ? "Today"
                : status === "upcoming"
                  ? dayDiff === 1
                    ? "Tomorrow"
                    : `In ${dayDiff} days`
                  : Math.abs(dayDiff) === 1
                    ? "Yesterday"
                    : `${Math.abs(dayDiff)} days ago`;
            const statusClass =
              status === "today"
                ? "bg-amber-100 text-amber-800 border-amber-200"
                : status === "upcoming"
                  ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                  : "bg-gray-100 text-gray-500 border-gray-200";
            return (
              <Card
                key={h.date}
                className={cn(
                  "bg-white/80 backdrop-blur-sm border-0 shadow-md hover:shadow-lg transition-all",
                  isEditing && "ring-2 ring-amber-300",
                  status === "passed" && "opacity-75",
                )}
              >
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                        status === "today"
                          ? "bg-amber-50 text-amber-600"
                          : status === "upcoming"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-gray-100 text-gray-400",
                      )}
                    >
                      <CalendarIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className={cn(
                          "text-sm font-semibold truncate",
                          status === "passed" ? "text-gray-500" : "text-gray-900",
                        )}
                      >
                        {h.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(h.date), "EEEE, MMM dd, yyyy")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge
                      className={cn(
                        "border text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap",
                        statusClass,
                      )}
                    >
                      {status === "upcoming" ? "Upcoming · " : status === "passed" ? "Passed · " : ""}
                      {statusLabel}
                    </Badge>
                    {canEditHolidays && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditHoliday(h)}
                          disabled={holidaySaving}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemoveHoliday(h.date)}
                          disabled={holidaySaving}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Remove
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderLeaveCard = (
    leave: Leave,
    showApprovalButtons = false,
    showApplicantName = showApprovalButtons,
  ) => {
    const isCompEarn = leave.leaveType === "compensatory_earn";
    const isMatEarn = leave.leaveType === "maternity_earn";
    const isPatEarn = leave.leaveType === "paternity_earn";
    const isAnyEarn = isCompEarn || isMatEarn || isPatEarn;
    const earnTitle = isCompEarn
      ? "Compensatory Leave Request"
      : isMatEarn
        ? "Maternity Leave Request"
        : isPatEarn
          ? "Paternity Leave Request"
          : null;
    return (
    <Card
      key={leave._id}
      className={cn(
        "bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300",
        isAnyEarn && "ring-1 ring-emerald-200",
      )}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {getStatusIcon(leave.status)}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                  {earnTitle ?? `${leave.leaveType.replace("_", " ")} Leave`}
                </h3>
                {showApplicantName && leave.employeeName && (
                  <p className="text-sm text-gray-600">
                    by {leave.employeeName}
                  </p>
                )}
              </div>
              {isAnyEarn && (
                <Badge className="border bg-emerald-50 text-emerald-700 border-emerald-200">
                  Earn request
                </Badge>
              )}
              <Badge className={cn("border", getStatusColor(leave.status))}>
                {leave.status?.charAt(0).toUpperCase() + leave.status.slice(1)}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
              <div>
                <span className="font-medium">Duration:</span>
                <br />
                {format(new Date(leave.startDate), "MMM dd, yyyy")} -{" "}
                {format(new Date(leave.endDate), "MMM dd, yyyy")}
                <div className="text-xs text-gray-500 mt-1">
                  {leave.days} day{leave.days === 1 ? "" : "s"}
                  {isAnyEarn && " (grant if approved)"}
                </div>
              </div>

              <div>
                <span className="font-medium">Applied:</span>
                <br />
                {leave.appliedDate
                  ? format(new Date(leave.appliedDate), "MMM dd, yyyy")
                  : "—"}
              </div>

              {leave.approvedBy && (
                <div>
                  <span className="font-medium">
                    Processed:
                  </span>
                  <br />
                  <span className="text-xs text-gray-500">by {leave.approvedBy}</span>
                </div>
              )}
            </div>

            {leave.reason && (
              <div className="mt-3">
                <span className="font-medium text-sm text-gray-700">
                  Reason:
                </span>
                <p className="text-sm text-gray-600 mt-1">{leave.reason}</p>
              </div>
            )}

            {leave.managerComments && (
              <div className="mt-3">
                <span className="font-medium text-sm text-gray-700">
                  Manager Comments:
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  {leave.managerComments}
                </p>
              </div>
            )}
          </div>

          <div className="ml-4 flex flex-col gap-2">
            {showApprovalButtons && leave.status === "pending" && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleApproveReject(leave, "approved")}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleApproveReject(leave, "rejected")}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </>
            )}

            {!showApprovalButtons &&
              (leave.status === "pending" || leave.status === "approved") &&
              (employeeID
                ? leave.employeeId === employeeID
                : leave.employeeId === user?._id) &&
              (() => {
                if (!leave.startDate) return false;
                const start = new Date(leave.startDate);
                start.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return today < start;
              })() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCancelLeave(leave._id)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Withdraw
                </Button>
              )}
          </div>
        </div>
      </CardContent>
    </Card>
    );
  };

  // Shared page size for every paginated leaves list.
  const LEAVES_PAGE_SIZE = 10;

  // Task-panel-style pager: "Previous · numbered window of 5 · Next", with a
  // "Page X of Y" line on mobile. Matches the Task list (see Task/TaskList.tsx)
  // so the Pending Approvals and All Team Requests tabs look consistent.
  const renderPager = (
    currentPage: number,
    totalPages: number,
    onChange: (p: number) => void,
  ) => {
    if (totalPages <= 1) return null;
    return (
      <>
        <div className="mt-8 flex justify-center items-center gap-2">
          <button
            type="button"
            onClick={() => onChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="gt-btn is-ghost is-sm"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>

          <div className="flex items-center gap-1.5">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;

              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => onChange(pageNum)}
                  className={currentPage === pageNum ? "gt-btn is-primary is-sm" : "gt-btn is-ghost is-sm"}
                  style={{ width: 36, height: 36, justifyContent: "center", padding: 0 }}
                >
                  <span className="gt-numerals">{pageNum}</span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => onChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="gt-btn is-ghost is-sm"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="text-center gt-page-sub gt-numerals md:hidden">
          Page {currentPage} of {totalPages}
        </div>
      </>
    );
  };

  // ── My Leaves: paginated list (10 per page) ─────────────────────────
  // Uses the shared Task-panel-style pager, same as the other leaves tabs.
  const renderMyLeavesPaginated = () => {
    const totalPages = Math.max(1, Math.ceil(filteredLeaves.length / LEAVES_PAGE_SIZE));
    const safePage = Math.min(myLeavesPage, totalPages);
    const pageItems = filteredLeaves.slice(
      (safePage - 1) * LEAVES_PAGE_SIZE,
      safePage * LEAVES_PAGE_SIZE,
    );
    return (
      <>
        <div className="grid gap-4">
          {pageItems.map((leave) => renderLeaveCard(leave, false))}
        </div>
        {renderPager(safePage, totalPages, setMyLeavesPage)}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
      {/* Header */}
      <div className="gt-page-hero flex-col md:flex-row gap-4 md:items-start">
        <div>
          <h1 className="gt-page-title">Leave <em>Management</em></h1>
          <p className="gt-page-sub" style={{ marginTop: 6 }}>
            {isManager
              ? <>Manage leave <em className="gt-serif-italic">requests</em> and view balances.</>
              : <>Manage your leave <em className="gt-serif-italic">requests</em> and view balances.</>}
          </p>
        </div>

        {/* Top-right action cluster — both Apply buttons live here so they
            sit together in the upper-right corner of the hero. */}
        <div className="md:ml-auto flex flex-col sm:flex-row gap-3 sm:items-start">

        {canApplyOwn && (
          <Dialog
            open={isApplyDialogOpen}
            onOpenChange={(open) => {
              setIsApplyDialogOpen(open);
              if (!open) resetApplyForm();
            }}
          >
            <DialogTrigger asChild>
              <Button className="gt-btn is-primary is-lg">
                <Plus className="h-4 w-4" />
                Apply for Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md overflow-visible">
              <DialogHeader>
                <DialogTitle>Apply for Leave</DialogTitle>
                <DialogDescription>
                  Submit a new leave request for approval
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="leaveType">Leave Type</Label>
                  <Select
                    value={newLeave.leaveType}
                    onValueChange={(value) =>
                      setNewLeave({ ...newLeave, leaveType: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select leave type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const balanceTypes = structuredBalance
                          ? (Object.keys(structuredBalance.balances) as LeaveType[])
                          : null;
                        const fallback: LeaveType[] = ["sick", "annual", "casual"];
                        const baseTypes: LeaveType[] = balanceTypes && balanceTypes.length
                          ? balanceTypes
                          : (() => {
                              const list: LeaveType[] = [...fallback];
                              if (leaveSettings?.maternity_enabled) list.push("maternity");
                              if (leaveSettings?.paternity_enabled) list.push("paternity");
                              if (leaveSettings?.compensatory_enabled) list.push("compensatory");
                              return list;
                            })();
                        const labels: Record<LeaveType, string> = {
                          sick: "Sick",
                          annual: "Annual",
                          casual: "Casual",
                          maternity: "Maternity",
                          paternity: "Paternity",
                          compensatory: "Compensatory",
                        };
                        return baseTypes.map((t) => {
                          const bal = structuredBalance?.balances[t];
                          const available =
                            typeof bal?.available === "number"
                              ? bal.available
                              : bal?.remaining;
                          return (
                            <SelectItem key={t} value={t}>
                              {labels[t]}
                              {typeof available === "number"
                                ? ` (${available} available)`
                                : ""}
                            </SelectItem>
                          );
                        });
                      })()}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStartOpen(true)}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate
                        ? format(startDate, "MMM dd, yyyy")
                        : "Pick a date"}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!startDate}
                      title={!startDate ? "Pick a start date first" : undefined}
                      onClick={() => setEndOpen(true)}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDate && "text-muted-foreground",
                        !startDate && "cursor-not-allowed opacity-60",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {!startDate
                        ? "Select start date first"
                        : endDate
                          ? format(endDate, "MMM dd, yyyy")
                          : "Pick a date"}
                    </Button>
                    {!startDate && (
                      <p className="text-[11px] text-gray-500">
                        Choose a start date to enable this.
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason</Label>
                  <Textarea
                    id="reason"
                    placeholder="Enter reason for leave..."
                    value={newLeave.reason}
                    onChange={(e) =>
                      setNewLeave({ ...newLeave, reason: e.target.value })
                    }
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsApplyDialogOpen(false);
                    resetApplyForm();
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleApplyLeave}>Submit Application</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Maternity-earn request — same earn-then-spend pattern as the
            compensatory flow. Only shown to female employees on enabled
            branches. Backend re-validates eligibility on submit. */}
        {leaveSettings?.maternity_enabled &&
          userGender === "female" &&
          canApplyOwn && (
            <Dialog
              open={isMatApplyOpen}
              onOpenChange={(open) => {
                setIsMatApplyOpen(open);
                if (!open) resetMatForm();
              }}
            >
              <DialogTrigger asChild>
                <Button className="gt-btn is-primary is-lg">
                  <Gift className="h-4 w-4" />
                  Request Maternity
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle>Request Maternity Leave</DialogTitle>
                  <DialogDescription>
                    Request your maternity entitlement. Your reporting person
                    will review and approve. Once approved, the days will
                    appear in your Maternity balance for you to apply against.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="mat-from">From</Label>
                      <Input
                        id="mat-from"
                        type="date"
                        value={matFromDate}
                        onChange={(e) => {
                          setMatFromDate(e.target.value);
                          const auto = daysBetween(e.target.value, matToDate);
                          if (auto > 0) setMatDays(String(auto));
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mat-to">To</Label>
                      <Input
                        id="mat-to"
                        type="date"
                        value={matToDate}
                        onChange={(e) => {
                          setMatToDate(e.target.value);
                          const auto = daysBetween(matFromDate, e.target.value);
                          if (auto > 0) setMatDays(String(auto));
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mat-days">Total days</Label>
                    <Input
                      id="mat-days"
                      type="number"
                      min={1}
                      value={matDays}
                      onChange={(e) => setMatDays(e.target.value)}
                      placeholder="e.g. 90"
                    />
                    <p className="text-[11px] text-gray-500">
                      Auto-calculated from the dates above. You can override
                      it if your entitlement differs.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mat-reason">
                      Note <span className="text-gray-400 text-xs">(optional)</span>
                    </Label>
                    <Textarea
                      id="mat-reason"
                      placeholder="Anything your reporting person should know…"
                      value={matReason}
                      onChange={(e) => setMatReason(e.target.value)}
                    />
                  </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsMatApplyOpen(false);
                      resetMatForm();
                    }}
                    disabled={matSubmitting}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleApplyParentalEarn("maternity")}
                    disabled={matSubmitting}
                    className="gt-btn is-primary w-full sm:w-auto"
                  >
                    {matSubmitting ? "Submitting…" : "Submit Request"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

        {/* Paternity-earn request — mirrors the maternity dialog. Only
            shown to male employees on enabled branches. */}
        {leaveSettings?.paternity_enabled &&
          userGender === "male" &&
          canApplyOwn && (
            <Dialog
              open={isPatApplyOpen}
              onOpenChange={(open) => {
                setIsPatApplyOpen(open);
                if (!open) resetPatForm();
              }}
            >
              <DialogTrigger asChild>
                <Button className="gt-btn is-primary is-lg">
                  <Gift className="h-4 w-4" />
                  Request Paternity
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle>Request Paternity Leave</DialogTitle>
                  <DialogDescription>
                    Request your paternity entitlement. Your reporting person
                    will review and approve. Once approved, the days will
                    appear in your Paternity balance for you to apply against.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="pat-from">From</Label>
                      <Input
                        id="pat-from"
                        type="date"
                        value={patFromDate}
                        onChange={(e) => {
                          setPatFromDate(e.target.value);
                          const auto = daysBetween(e.target.value, patToDate);
                          if (auto > 0) setPatDays(String(auto));
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pat-to">To</Label>
                      <Input
                        id="pat-to"
                        type="date"
                        value={patToDate}
                        onChange={(e) => {
                          setPatToDate(e.target.value);
                          const auto = daysBetween(patFromDate, e.target.value);
                          if (auto > 0) setPatDays(String(auto));
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pat-days">Total days</Label>
                    <Input
                      id="pat-days"
                      type="number"
                      min={1}
                      value={patDays}
                      onChange={(e) => setPatDays(e.target.value)}
                      placeholder="e.g. 15"
                    />
                    <p className="text-[11px] text-gray-500">
                      Auto-calculated from the dates above. You can override
                      it if your entitlement differs.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pat-reason">
                      Note <span className="text-gray-400 text-xs">(optional)</span>
                    </Label>
                    <Textarea
                      id="pat-reason"
                      placeholder="Anything your reporting person should know…"
                      value={patReason}
                      onChange={(e) => setPatReason(e.target.value)}
                    />
                  </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsPatApplyOpen(false);
                      resetPatForm();
                    }}
                    disabled={patSubmitting}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleApplyParentalEarn("paternity")}
                    disabled={patSubmitting}
                    className="gt-btn is-primary w-full sm:w-auto"
                  >
                    {patSubmitting ? "Submitting…" : "Submit Request"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

        {/* Compensatory-earn request: separate dialog, shown only when the
            feature is enabled on this branch. Asks the manager to grant a
            comp leave because the employee worked on a holiday. */}
        {leaveSettings?.compensatory_enabled &&
          canApplyOwn && (
            <Dialog
              open={isCompApplyOpen}
              onOpenChange={(open) => {
                setIsCompApplyOpen(open);
                if (!open) resetCompForm();
              }}
            >
              <DialogTrigger asChild>
                <Button className="gt-btn is-primary is-lg">
                  <Gift className="h-4 w-4" />
                  Request Compensatory
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle>Request Compensatory Leave</DialogTitle>
                  <DialogDescription>
                    Request compensatory days for working on holidays. Your
                    reporting person will review and approve. Once approved,
                    the days will appear in your Compensatory balance for you
                    to apply against.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="comp-from">From</Label>
                      <Input
                        id="comp-from"
                        type="date"
                        value={compFromDate}
                        onChange={(e) => {
                          setCompFromDate(e.target.value);
                          const auto = daysBetween(e.target.value, compToDate);
                          if (auto > 0) setCompDays(String(auto));
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="comp-to">To</Label>
                      <Input
                        id="comp-to"
                        type="date"
                        value={compToDate}
                        onChange={(e) => {
                          setCompToDate(e.target.value);
                          const auto = daysBetween(compFromDate, e.target.value);
                          if (auto > 0) setCompDays(String(auto));
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comp-days">Total days</Label>
                    <Input
                      id="comp-days"
                      type="number"
                      min={1}
                      value={compDays}
                      onChange={(e) => setCompDays(e.target.value)}
                      placeholder="e.g. 2"
                    />
                    <p className="text-[11px] text-gray-500">
                      Auto-calculated from the dates above. You can override
                      it if it differs.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comp-reason">
                      Note <span className="text-gray-400 text-xs">(optional)</span>
                    </Label>
                    <Textarea
                      id="comp-reason"
                      placeholder="Anything your reporting person should know…"
                      value={compReason}
                      onChange={(e) => setCompReason(e.target.value)}
                    />
                  </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCompApplyOpen(false);
                      resetCompForm();
                    }}
                    disabled={compSubmitting}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApplyCompensatory}
                    disabled={compSubmitting}
                    className="gt-btn is-primary w-full sm:w-auto"
                  >
                    {compSubmitting ? "Submitting…" : "Submit Request"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

        </div>
        {/* /top-right action cluster */}

        {/* Centered date-picker overlay — portaled to document.body so it
            always sits above the Apply Leave dialog (which uses its own
            Radix portal) and is centered in the viewport regardless of
            where the trigger sits inside the form. */}
        {(startOpen || endOpen) && createPortal(
          (
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
              // Radix Dialog (the Apply Leave dialog) sets pointer-events:
              // none on <body> while it's open. Because this portal is a
              // child of <body>, we have to opt back in explicitly or the
              // calendar day clicks are eaten.
              style={{ zIndex: 9999, pointerEvents: 'auto' }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                setStartOpen(false);
                setEndOpen(false);
              }}
            >
              <div
                className="bg-white rounded-xl shadow-2xl p-2"
                style={{ minWidth: 300, pointerEvents: 'auto' }}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-3 pt-2 pb-1">
                  <span className="text-sm font-semibold text-gray-800">
                    {startOpen ? 'Pick start date' : 'Pick end date'}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setStartOpen(false);
                      setEndOpen(false);
                    }}
                    aria-label="Close"
                    className="p-1 rounded hover:bg-gray-100 text-gray-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {startOpen ? (
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setStartOpen(false);
                      if (date && endDate && endDate < date) {
                        setEndDate(undefined);
                      }
                    }}
                    initialFocus
                  />
                ) : (
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      if (date && startDate && date < startDate) return;
                      setEndDate(date);
                      setEndOpen(false);
                    }}
                    disabled={startDate ? { before: startDate } : undefined}
                    defaultMonth={startDate}
                    initialFocus
                  />
                )}
              </div>
            </div>
          ),
          document.body,
        )}
      </div>

      {/* Leave Balance Cards */}
      {structuredBalance && Object.keys(structuredBalance.balances).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(Object.entries(structuredBalance.balances) as [LeaveType, any][]).map(
            ([type, entry]) => (
              <div key={type} className="gt-stat">
                <div className="gt-stat-label" style={{ textTransform: "capitalize" }}>
                  {type}
                </div>
                <p className="gt-stat-value">{entry.remaining}</p>
                <p className="gt-page-sub" style={{ fontSize: 12, marginTop: 2 }}>
                  of {entry.allocated} days
                  {entry.reserved_pending > 0
                    ? ` · ${entry.reserved_pending} pending`
                    : ""}
                </p>
              </div>
            )
          )}
        </div>
      )}

      {/* Approval Dialog */}
      <Dialog
        open={isApprovalDialogOpen}
        onOpenChange={setIsApprovalDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {approvalAction === "approved" ? "Approve" : "Reject"} Leave
              Request
            </DialogTitle>
            <DialogDescription>
              {selectedLeave && (
                <>
                  {approvalAction === "approved" ? "Approve" : "Reject"} leave
                  request from {selectedLeave.employeeName} for{" "}
                  {selectedLeave.leaveType} leave from{" "}
                  {format(new Date(selectedLeave.startDate), "MMM dd, yyyy")} to{" "}
                  {format(new Date(selectedLeave.endDate), "MMM dd, yyyy")}.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="comments">
                Comments{" "}
                {approvalAction === "rejected" && (
                  <span className="text-red-500"></span>
                )}
              </Label>
              <Textarea
                id="comments"
                placeholder={
                  approvalAction === "approved"
                    ? "Add any comments (optional)..."
                    : "Please provide a reason for rejection (optional)..."
                }
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                className="min-h-20"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsApprovalDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprovalSubmit}
              // disabled={
              //   approvalAction === "rejected" && !approvalComments.trim()
              // }
              className={
                approvalAction === "approved"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
            >
              {approvalAction === "approved" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      {isTeamViewer ? (
        // <Tabs defaultValue="pending" className="space-y-6">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value)}
          className="space-y-6"
        >
          <TabsList
            className="grid w-full"
            style={{
              gridTemplateColumns: `repeat(${
                [showApprovalsTab, showTeamTab, showMyTab, showHolidaysTab].filter(Boolean).length || 1
              }, minmax(0, 1fr))`,
            }}
          >
            {showApprovalsTab && (
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Pending Approvals ({pendingLeaves.length})
              </TabsTrigger>
            )}

            {showTeamTab && (
              <TabsTrigger value="all">All Team Requests</TabsTrigger>
            )}

            {showMyTab && <TabsTrigger value="my">My Leaves</TabsTrigger>}

            {showHolidaysTab && (
              <TabsTrigger value="holidays" className="flex items-center gap-2">
                <PartyPopper className="h-4 w-4" />
                Holidays
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                  Pending Leave Requests
                </CardTitle>
                <CardDescription>
                  Review and approve or reject leave requests from your team
                  members
                </CardDescription>
              </CardHeader>
            </Card>

            {pendingLeaves.length === 0 ? (
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No pending requests
                  </h3>
                  <p className="text-gray-600 text-center">
                    All leave requests have been processed.
                  </p>
                </CardContent>
              </Card>
            ) : (
              (() => {
                const totalPages = Math.max(1, Math.ceil(pendingLeaves.length / LEAVES_PAGE_SIZE));
                const safePage = Math.min(pendingPage, totalPages);
                const pageItems = pendingLeaves.slice(
                  (safePage - 1) * LEAVES_PAGE_SIZE,
                  safePage * LEAVES_PAGE_SIZE,
                );
                return (
                  <>
                    <div className="grid gap-4">
                      {pageItems.map((leave) => renderLeaveCard(leave, canApprove))}
                    </div>
                    {renderPager(safePage, totalPages, setPendingPage)}
                  </>
                );
              })()
            )}
          </TabsContent>

          <TabsContent value="all" className="space-y-4">
            {/* Filters */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-blue-600" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={filterStatus}
                      onValueChange={setFilterStatus}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Leave Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="sick">Sick</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        {leaveSettings?.paternity_enabled && (
                          <SelectItem value="paternity">Paternity</SelectItem>
                        )}
                        {leaveSettings?.maternity_enabled && (
                          <SelectItem value="maternity">Maternity</SelectItem>
                        )}
                        {leaveSettings?.compensatory_enabled && (
                          <SelectItem value="compensatory">Compensatory</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                All Team Leave Requests
              </h2>

              {filteredAllLeaves.length === 0 ? (
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No leave requests found
                    </h3>
                    <p className="text-gray-600 text-center">
                      No leave requests match your current filters.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                (() => {
                  const totalPages = Math.max(1, Math.ceil(filteredAllLeaves.length / LEAVES_PAGE_SIZE));
                  const safePage = Math.min(allTeamPage, totalPages);
                  const pageItems = filteredAllLeaves.slice(
                    (safePage - 1) * LEAVES_PAGE_SIZE,
                    safePage * LEAVES_PAGE_SIZE,
                  );
                  return (
                    <>
                      <div className="grid gap-4">
                        {pageItems.map((leave) =>
                          renderLeaveCard(
                            leave,
                            // No approve/reject on your OWN leave — it routes up to
                            // your manager / admin, not back to you.
                            canApproveFromTeam &&
                              leave.status === "pending" &&
                              leave.employeeId !== employeeID,
                            true,
                          )
                        )}
                      </div>
                      {renderPager(safePage, totalPages, setAllTeamPage)}
                    </>
                  );
                })()
              )}
            </div>
          </TabsContent>

          <TabsContent value="my" className="space-y-4">
            {/* Leave Balance Cards for managers */}
            {user?.role === "manager" && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(leaveBalance).map(([type, balance]) => (
                  <Card
                    key={type}
                    className="bg-white/80 backdrop-blur-sm border-0 shadow-lg"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600 capitalize">
                        {type.replace("_", " ")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-gray-900">
                        {balance}
                      </div>
                      <p className="text-xs text-gray-500">days remaining</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Filters */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-blue-600" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={filterStatus}
                      onValueChange={setFilterStatus}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Leave Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="sick">Sick</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        {leaveSettings?.paternity_enabled &&
                          userGender === "male" && (
                          <SelectItem value="paternity">Paternity</SelectItem>
                        )}
                        {leaveSettings?.maternity_enabled &&
                          userGender === "female" && (
                          <SelectItem value="maternity">Maternity</SelectItem>
                        )}
                        {leaveSettings?.compensatory_enabled && (
                          <SelectItem value="compensatory">Compensatory</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                My Leave Requests
              </h2>

              {tabLoading ? (
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="flex items-center justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </CardContent>
                </Card>
              ) : filteredLeaves.length === 0 ? (
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No leave requests found
                    </h3>
                    <p className="text-gray-600 text-center">
                      {leaves.length === 0
                        ? "You haven't submitted any leave requests yet."
                        : "No leave requests match your current filters."}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                renderMyLeavesPaginated()
              )}
            </div>
          </TabsContent>

          <TabsContent value="holidays" className="space-y-4">
            {renderHolidaysTab()}
          </TabsContent>
        </Tabs>
      ) : (
        // Employee view — own leaves + read-only holidays
        <Tabs
          value={activeTab === "holidays" || !showMyTab ? "holidays" : "my"}
          onValueChange={(value) => setActiveTab(value)}
          className="space-y-6"
        >
          <TabsList
            className="grid w-full"
            style={{
              gridTemplateColumns: `repeat(${
                [showMyTab, showHolidaysTab].filter(Boolean).length || 1
              }, minmax(0, 1fr))`,
            }}
          >
            {showMyTab && <TabsTrigger value="my">My Leaves</TabsTrigger>}
            {showHolidaysTab && (
              <TabsTrigger value="holidays" className="flex items-center gap-2">
                <PartyPopper className="h-4 w-4" />
                Holidays
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="my" className="space-y-4">
            {/* Filters */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-blue-600" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Leave Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="sick">Sick</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        {leaveSettings?.paternity_enabled &&
                          userGender === "male" && (
                          <SelectItem value="paternity">Paternity</SelectItem>
                        )}
                        {leaveSettings?.maternity_enabled &&
                          userGender === "female" && (
                          <SelectItem value="maternity">Maternity</SelectItem>
                        )}
                        {leaveSettings?.compensatory_enabled && (
                          <SelectItem value="compensatory">Compensatory</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Leave Requests List */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Your Leave Requests
              </h2>

              {filteredLeaves.length === 0 ? (
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No leave requests found
                    </h3>
                    <p className="text-gray-600 text-center">
                      {leaves.length === 0
                        ? "You haven't submitted any leave requests yet."
                        : "No leave requests match your current filters."}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                renderMyLeavesPaginated()
              )}
            </div>
          </TabsContent>

          <TabsContent value="holidays" className="space-y-4">
            {renderHolidaysTab()}
          </TabsContent>
        </Tabs>
      )}

      {/* Apply error / insufficient-balance popup */}
      <Dialog
        open={applyError.open}
        onOpenChange={(open) => setApplyError((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Cannot submit leave</DialogTitle>
            <DialogDescription>{applyError.message}</DialogDescription>
          </DialogHeader>
          {typeof applyError.balance_remaining === "number" && (
            <div className="mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <strong>{applyError.balance_remaining}</strong>{" "}
              day{applyError.balance_remaining === 1 ? "" : "s"} of{" "}
              {applyError.leave_type || "this type"} leave remaining.
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setApplyError({ open: false, message: "" })}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
